import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import {
  Banner,
  Card,
  CardBody,
  CardFoot,
  CardHead,
  KV,
  KVRow,
  LoadError,
  MeterRow,
  PageHead,
  Seg,
  Severity,
  Skeleton,
  Stat,
  StateBlock,
} from '../components/ui.tsx'
import { usePageTitle } from '../layout/AppShell.tsx'
import { post, useApi } from '../lib/api.ts'
import { ago, clock, count, gbp, pct } from '../lib/format.ts'
import type { LimitView, RiskPayload } from '../../shared/types.ts'

type EventFilter = 'all' | 'vetoes' | 'halts'

/**
 * The gate every order passes through. Sixteen hard limits, checked on each intent.
 *
 * The meters show consumption, not headroom: closer to full is closer to a veto. A limit
 * at 100% blocks the next qualifying intent and fires its action.
 */
export function RiskCentre() {
  usePageTitle('Risk centre')
  const { data, error, loading, reload } = useApi<RiskPayload>('/risk', 6000)
  const [eventFilter, setEventFilter] = useState<EventFilter>('all')

  if (error) return <main className="content"><LoadError error={error} onRetry={reload} /></main>
  if (loading || !data) {
    return (
      <main className="content">
        <Card>
          <CardBody>
            <Skeleton rows={10} />
          </CardBody>
        </Card>
      </main>
    )
  }

  const s = data.summary
  const halted = data.state !== 'trading'
  const categories = [...new Set(data.limits.map((l) => l.category))]
  const events = data.events.filter((e) =>
    eventFilter === 'all' ? true : eventFilter === 'vetoes' ? e.action === 'REJECT' : e.action !== 'REJECT',
  )

  const control = async (action: 'halt' | 'flatten' | 'resume') => {
    await post('/risk/controls', { action })
    reload()
  }

  return (
    <main className="content">
      <PageHead
        title="Risk centre"
        sub="The gate every order passes through. Sixteen hard limits, checked on each intent — a breach is the system working, not an error."
        actions={
          <>
            <button className="btn danger-soft" onClick={() => void control('halt')} disabled={halted}>
              Halt trading
            </button>
            <button className="btn danger" onClick={() => void control('flatten')}>
              Flatten &amp; halt
            </button>
          </>
        }
      />

      {halted ? (
        <Banner
          tone="flatten"
          icon="■"
          title={
            data.haltDetail
              ? `${data.haltDetail.action} — ${data.haltDetail.limitName} breached`
              : `Trading is ${data.state}`
          }
          actions={
            <span className={`state-pill ${data.state}`}>
              <span className="st-dot" />
              {data.stateCause ?? data.state}
            </span>
          }
        >
          {data.haltDetail ? (
            <>
              <span className="mono">{data.haltDetail.limitName}</span> tripped at{' '}
              <span className="mono">{clock(data.haltDetail.trippedAt)}</span> — threshold{' '}
              <b className="mono">{data.haltDetail.threshold ?? '—'}</b>, observed{' '}
              <b className="mono">{data.haltDetail.observed ?? '—'}</b>. Trading is stopped until you resume. Resuming
              does not reset the daily loss counter, which clears at 00:00 UTC.
            </>
          ) : (
            <>
              {data.stateCause ?? 'The engine is not accepting new intents.'} Positions are unaffected until you
              flatten.
            </>
          )}
        </Banner>
      ) : (
        <Banner
          tone="success"
          icon="●"
          title="Trading — the risk gate is armed and passing intents"
          actions={
            <span className="state-pill trading">
              <span className="st-dot" />
              Trading
            </span>
          }
        >
          All sixteen limits within bounds. Last intent evaluated {ago(data.lastIntentAgoS)} · {data.checksToday} checks
          today · {data.vetoedToday} vetoed.
          {data.hotLimits.length > 0 && (
            <>
              {' '}
              Running hot:{' '}
              {data.hotLimits.slice(0, 3).map((h, i) => (
                <span key={h.name}>
                  {i > 0 && ', '}
                  <b className="mono">{h.name}</b> ({h.pct}%)
                </span>
              ))}
              .
            </>
          )}
        </Banner>
      )}

      <div className="grid cols-4" style={{ marginBottom: 16 }}>
        <Card pad>
          <Stat
            label="Daily loss budget remaining"
            value={<span style={{ color: 'var(--neg)' }}>{gbp(s.dailyLossRemaining)}</span>}
            meter={{ pct: s.dailyLossPct, tone: 'neg' }}
            sub={
              <>
                {gbp(-s.dailyLossUsed)} intraday of {gbp(-(s.dailyLossLimit ?? 0))} · <b>{pct(s.dailyLossPct)} used</b>
              </>
            }
          />
        </Card>
        <Card pad>
          <Stat
            label="Liability at risk now"
            value={gbp(s.liability)}
            meter={{ pct: s.liabilityPct }}
            sub={`${pct(s.liabilityPct)} of ${gbp(s.liabilityLimit)} portfolio ceiling`}
          />
        </Card>
        <Card pad>
          <Stat
            label="Open markets"
            value={
              <>
                {s.openMarkets}{' '}
                <span className="u-faint" style={{ fontSize: 'var(--fs-lg)' }}>
                  / {s.openMarketsLimit ?? '—'}
                </span>
              </>
            }
            meter={{ pct: s.openMarketsPct }}
            sub={`${Math.max(0, (s.openMarketsLimit ?? 0) - s.openMarkets)} slots free`}
          />
        </Card>
        <Card pad>
          <Stat
            label="Transactions today"
            value={
              <>
                {count(s.transactions)}{' '}
                <span className="u-faint" style={{ fontSize: 'var(--fs-lg)' }}>
                  / {count(s.transactionsLimit)}
                </span>
              </>
            }
            meter={{ pct: s.transactionsPct }}
            sub="Rate-limit guard · HALT on breach"
          />
        </Card>
      </div>

      <div className="grid cols-12">
        <div className="span-8 stack">
          <Card>
            <CardHead
              title="Active risk limits"
              sub={`${data.limits.length} armed · live headroom against each`}
              right={
                <div className="row" style={{ gap: 14 }}>
                  <span className="row" style={{ gap: 6 }}>
                    <Severity action="REJECT" />
                    <span className="cell-sub">this order</span>
                  </span>
                  <span className="row" style={{ gap: 6 }}>
                    <Severity action="HALT" />
                    <span className="cell-sub">open nothing new</span>
                  </span>
                  <span className="row" style={{ gap: 6 }}>
                    <Severity action="FLATTEN_AND_HALT" />
                    <span className="cell-sub">close all, stop</span>
                  </span>
                </div>
              }
            />
            <CardBody>
              {categories.map((cat, ci) => {
                const group = data.limits.filter((l) => l.category === cat)
                return (
                  <div key={cat}>
                    <div className="lbl" style={{ margin: ci === 0 ? '0 0 12px' : '18px 0 12px' }}>
                      {cat}
                    </div>
                    {group.map((l, i) => (
                      <MeterRow
                        key={l.name}
                        last={ci === categories.length - 1 && i === group.length - 1}
                        label={
                          <>
                            <span className="mono">{l.name}</span> <Severity action={l.action} />
                          </>
                        }
                        value={l.valueLabel}
                        pct={l.pct}
                      />
                    ))}
                  </div>
                )
              })}
            </CardBody>
            <CardFoot>
              Meters show consumption — closer to full is closer to a veto. A limit at 100% blocks the next qualifying
              intent and fires its action.
            </CardFoot>
          </Card>

          <Card>
            <CardHead
              title="Risk-event history"
              sub="Every threshold touched today"
              right={
                <Seg
                  label="Event filter"
                  value={eventFilter}
                  onChange={setEventFilter}
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'vetoes', label: 'Vetoes' },
                    { value: 'halts', label: 'Halts' },
                  ]}
                />
              }
            />
            <CardBody flush>
              {events.length === 0 ? (
                <StateBlock
                  icon="◇"
                  title="No limit was touched today"
                  text="Nothing has come close enough to a threshold to fire. That is not automatically good news — a day with zero events often means the limits are set too loose to bind."
                />
              ) : (
                <div className="table-wrap">
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Limit</th>
                        <th>Action</th>
                        <th className="num">Threshold</th>
                        <th className="num">Observed</th>
                        <th>Market</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((e) => (
                        <tr key={e.id}>
                          <td className="u-mono">{clock(e.ts)}</td>
                          <td className="mono">{e.limitName}</td>
                          <td>
                            <Severity action={e.action} />
                          </td>
                          <td className="num">{e.thresholdLabel}</td>
                          <td className="num neg">{e.observedLabel}</td>
                          <td>{e.market}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
            <CardFoot>
              {data.eventSummary.total} events today · {data.eventSummary.rejects} rejects · {data.eventSummary.halts}{' '}
              halts · {data.eventSummary.flattens} flatten-and-halts. History is retained for audit and settlement
              dispute.
            </CardFoot>
          </Card>
        </div>

        <div className="span-4 stack">
          <LimitEditor limits={data.limits} onSaved={reload} />

          <Card>
            <CardHead title="Manual controls" />
            <CardBody className="stack sm">
              <div className="between">
                <div>
                  <div className="cell-strong">Halt</div>
                  <div className="cell-sub">Stop opening new positions</div>
                </div>
                <button className="btn danger-soft sm" onClick={() => void control('halt')} disabled={halted}>
                  Halt
                </button>
              </div>
              <hr className="hr" style={{ margin: '4px 0' }} />
              <div className="between">
                <div>
                  <div className="cell-strong">Flatten &amp; halt</div>
                  <div className="cell-sub">Close everything at market, then stop</div>
                </div>
                <button className="btn danger sm" onClick={() => void control('flatten')}>
                  Flatten
                </button>
              </div>
              <hr className="hr" style={{ margin: '4px 0' }} />
              <div className="between">
                <div>
                  <div className="cell-strong">Resume</div>
                  <div className="cell-sub">Re-arm the gate · available only when halted</div>
                </div>
                <button className="btn sm" onClick={() => void control('resume')} disabled={!halted}>
                  Resume
                </button>
              </div>
            </CardBody>
            <CardFoot>Every manual control is logged with operator and timestamp.</CardFoot>
          </Card>

          {halted && data.haltDetail && (
            <Card>
              <CardHead title="What halted us" />
              <CardBody>
                <KV>
                  <KVRow label="Limit" text>
                    <span className="mono">{data.haltDetail.limitName}</span>
                  </KVRow>
                  <KVRow label="Action" text>
                    <Severity action={data.haltDetail.action} />
                  </KVRow>
                  <KVRow label="Threshold">{data.haltDetail.threshold ?? '—'}</KVRow>
                  <KVRow label="Observed">
                    <span className="neg">{data.haltDetail.observed ?? '—'}</span>
                  </KVRow>
                  <KVRow label="Tripped at">{clock(data.haltDetail.trippedAt)}</KVRow>
                </KV>
              </CardBody>
              <CardFoot>
                The daily counter resets at 00:00 UTC. Until then, resuming re-arms the gate but the loss budget stays
                consumed — the next loss re-trips immediately.
              </CardFoot>
            </Card>
          )}
        </div>
      </div>

      <p className="u-faint u-sm" style={{ marginTop: 24 }}>
        Limits enforce identically in paper and live. <Link to="/system-health">Why trading can be blocked →</Link>
      </p>
    </main>
  )
}

/**
 * Editing a limit. Lowering one needs no reason; raising one always does, because raising
 * it is the only edit that widens how much can be at risk.
 */
function LimitEditor({ limits, onSaved }: { limits: LimitView[]; onSaved: () => void }) {
  const editable = useMemo(() => limits.filter((l) => l.editable && l.threshold !== null), [limits])
  const [name, setName] = useState(editable[0]?.name ?? '')
  const [value, setValue] = useState('')
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)

  const current = editable.find((l) => l.name === name)

  useEffect(() => {
    if (current && value === '') setValue(String(current.threshold ?? ''))
  }, [current, value])

  if (editable.length === 0) return null

  const next = Number(value)
  const raising = current?.threshold !== null && Number.isFinite(next) && next > (current?.threshold ?? 0)
  const money = current?.unit === 'money'

  const save = async () => {
    setSaving(true)
    setMessage(null)
    try {
      await post('/risk/limits', { name, threshold: next, reason })
      setMessage({ ok: true, text: 'Limit saved and the change logged.' })
      setReason('')
      onSaved()
    } catch (err) {
      setMessage({ ok: false, text: err instanceof Error ? err.message : String(err) })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHead title="Edit a limit" sub="Changes apply to the next intent" />
      <CardBody>
        <div className="field">
          <label htmlFor="limit-name">Limit</label>
          <select
            id="limit-name"
            className="select"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              setValue(String(editable.find((l) => l.name === e.target.value)?.threshold ?? ''))
              setMessage(null)
            }}
          >
            {editable.map((l) => (
              <option key={l.name} value={l.name}>
                {l.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="limit-current">Current threshold</label>
          <div className="input-group">
            {money && <span className="addon">£</span>}
            <input id="limit-current" className="input mono" value={current?.threshold ?? ''} disabled />
          </div>
        </div>

        <div className="field">
          <label htmlFor="limit-next">New threshold</label>
          <div className="input-group">
            {money && <span className="addon">£</span>}
            <input
              id="limit-next"
              className="input mono"
              value={value}
              inputMode="decimal"
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
          {raising && (
            <div className="help warn">
              ◆ This <b>raises</b> the limit — you are widening how much can be at risk. A written reason is required
              and is kept with the change.
            </div>
          )}
        </div>

        {raising && (
          <div className="field">
            <label htmlFor="limit-reason">Reason for raising (required)</label>
            <textarea
              id="limit-reason"
              className="input"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Increasing the market ceiling for the Champions League slate — deeper books, tighter spreads, edge holds at size."
            />
            <div className="help">Lowering a limit needs no reason. Raising one always does.</div>
          </div>
        )}

        {current && Number.isFinite(next) && next !== current.threshold && (
          <Banner tone={raising ? 'degraded' : 'incident'} title="Confirm the change" flush>
            <span className="mono">{current.name}</span>{' '}
            <span className="mono">
              {money ? gbp(current.threshold) : current.threshold} → {money ? gbp(next) : next}
            </span>
            . Open positions are re-checked against the new value immediately.
          </Banner>
        )}

        {message && (
          <div className={`help ${message.ok ? '' : 'warn'}`} style={{ marginTop: 10 }} role="status">
            {message.text}
          </div>
        )}

        <button
          className="btn primary block"
          style={{ marginTop: 14 }}
          disabled={saving || !Number.isFinite(next) || next === current?.threshold}
          onClick={() => void save()}
        >
          {raising ? 'Save limit & log reason' : 'Save limit'}
        </button>
      </CardBody>
    </Card>
  )
}
